import React, {useState} from "react";
import {C} from "../styles";
import {formatDate} from "../utils";
import {apiSendHelpRequest, apiGetMyHelpRequests, apiAddHelpRequestMessage} from "../api/help";
import Modal from "./Modal";

export default function HelpButton({hasUnreadReply = false, onRepliesRead = () => {}}) {
    const [open, setOpen] = useState(false);
    const [view, setView] = useState("new");
    const [message, setMessage] = useState("");
    const [phone, setPhone] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [myRequests, setMyRequests] = useState([]);
    const [loadingMine, setLoadingMine] = useState(false);
    const [replyDrafts, setReplyDrafts] = useState({});
    const [sendingReplyFor, setSendingReplyFor] = useState(null);

    function close() {
        setOpen(false);
        setView("new");
        setMessage("");
        setPhone("");
        setSent(false);
        setError("");
    }

    async function send() {
        if (message.trim() === "" || sending) return;
        setSending(true);
        setError("");
        const data = await apiSendHelpRequest(message.trim(), phone.trim());
        setSending(false);
        if (data.error) { setError(data.error); return; }
        setSent(true);
    }

    function openMine() {
        setView("mine");
        setLoadingMine(true);
        apiGetMyHelpRequests().then(data => {
            setMyRequests(Array.isArray(data) ? data : []);
            setLoadingMine(false);
            onRepliesRead();
        });
    }

    function sendReply(requestId) {
        const content = (replyDrafts[requestId] || "").trim();
        if (content === "" || sendingReplyFor === requestId) return;
        setSendingReplyFor(requestId);
        apiAddHelpRequestMessage(requestId, content).then(() => {
            setSendingReplyFor(null);
            setReplyDrafts(prev => ({...prev, [requestId]: ""}));
            apiGetMyHelpRequests().then(data => setMyRequests(Array.isArray(data) ? data : []));
        });
    }

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                title="J'ai besoin d'aide"
                aria-label="J'ai besoin d'aide"
                style={{
                    background: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    borderRadius: "50%",
                    width: 56,
                    height: 56,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    flexShrink: 0,
                    lineHeight: 1,
                    position: "relative",
                }}
            >
                <span style={{fontSize: 24}}>🛟</span>
                <span style={{fontSize: 10, fontWeight: 800, letterSpacing: 0.5, marginTop: 2}}>SOS</span>
                {hasUnreadReply && (
                    <span style={{
                        position: "absolute", top: 3, right: 3,
                        width: 12, height: 12, borderRadius: "50%",
                        backgroundColor: C.danger, border: `2px solid ${C.primaryDark}`,
                    }}/>
                )}
            </button>

            {open && (
                <Modal title="🛟 Besoin d'aide" onClose={close} scrollable>
                        <div style={{display: "flex", gap: 6, marginBottom: 16, borderBottom: `1px solid ${C.border}`}}>
                            <button
                                onClick={() => setView("new")}
                                style={{
                                    background: "none", border: "none", cursor: "pointer",
                                    padding: "8px 4px", fontSize: 13, fontWeight: 600,
                                    color: view === "new" ? C.primary : C.textMuted,
                                    borderBottom: view === "new" ? `2px solid ${C.primary}` : "2px solid transparent",
                                }}
                            >
                                Nouvelle demande
                            </button>
                            <button
                                onClick={openMine}
                                style={{
                                    background: "none", border: "none", cursor: "pointer",
                                    padding: "8px 4px", fontSize: 13, fontWeight: 600,
                                    color: view === "mine" ? C.primary : C.textMuted,
                                    borderBottom: view === "mine" ? `2px solid ${C.primary}` : "2px solid transparent",
                                    position: "relative",
                                }}
                            >
                                Mes demandes
                                {hasUnreadReply && (
                                    <span style={{
                                        position: "absolute", top: 4, right: -8,
                                        width: 8, height: 8, borderRadius: "50%",
                                        backgroundColor: C.danger,
                                    }}/>
                                )}
                            </button>
                        </div>

                        {view === "new" ? (
                            !sent ? (
                                <>
                                    <p style={{fontSize: 13, color: C.textMuted, marginBottom: 14, lineHeight: "19px"}}>
                                        Une situation difficile, un conflit, du harcèlement ? Écrivez-le ici.
                                        Ce message part directement au SAPPM avec votre <strong>pseudonyme anonyme</strong> — jamais votre identité réelle.
                                    </p>
                                    <textarea
                                        autoFocus
                                        value={message}
                                        onChange={e => setMessage(e.target.value)}
                                        placeholder="Décrivez votre situation…"
                                        style={{
                                            width: "100%", height: 110, padding: "10px 12px", marginBottom: 10,
                                            borderRadius: 10, border: `1.5px solid ${C.border}`, boxSizing: "border-box",
                                            fontSize: 14, color: C.text, resize: "vertical", fontFamily: "inherit",
                                        }}
                                    />
                                    <label style={{fontSize: 12, color: C.textMuted, display: "block", marginBottom: 6}}>
                                        Optionnel : Si vous souhaitez être recontacté par téléphone laissez nous un numéro
                                    </label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        placeholder="06 12 34 56 78"
                                        style={{
                                            width: "100%", padding: "9px 12px", marginBottom: 10,
                                            borderRadius: 10, border: `1.5px solid ${C.border}`, boxSizing: "border-box",
                                            fontSize: 14, color: C.text, fontFamily: "inherit",
                                        }}
                                    />
                                    {error && <p style={{color: C.danger, fontSize: 13, marginBottom: 8, marginTop: -4}}>{error}</p>}
                                    <button
                                        onClick={send}
                                        disabled={message.trim() === "" || sending}
                                        style={{
                                            ...{
                                                backgroundColor: C.primary, color: "white", border: "none",
                                                padding: "9px 20px", borderRadius: 10, cursor: "pointer",
                                                fontWeight: 600, fontSize: 14, width: "100%",
                                            },
                                            opacity: (message.trim() === "" || sending) ? 0.6 : 1,
                                            cursor: (message.trim() === "" || sending) ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        {sending ? "Envoi…" : "Envoyer au SAPPM"}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <h3 style={{margin: "0 0 8px", fontFamily: "'Fraunces', serif", color: C.primaryDark}}>
                                        Message transmis
                                    </h3>
                                    <p style={{fontSize: 14, color: C.text, marginBottom: 18, lineHeight: "20px"}}>
                                        Votre demande a été transmise au SAPPM. Un représentant reviendra vers vous si nécessaire.
                                    </p>
                                    <button
                                        onClick={close}
                                        style={{
                                            backgroundColor: C.primaryLight, color: C.primary, border: `1px solid ${C.primary}33`,
                                            padding: "9px 20px", borderRadius: 10, cursor: "pointer",
                                            fontWeight: 600, fontSize: 14, width: "100%",
                                        }}
                                    >
                                        Fermer
                                    </button>
                                </>
                            )
                        ) : (
                            <>
                                {loadingMine ? (
                                    <p style={{fontSize: 13, color: C.textMuted}}>Chargement…</p>
                                ) : myRequests.length === 0 ? (
                                    <p style={{fontSize: 13, color: C.textMuted}}>Vous n'avez pas encore fait de demande d'aide.</p>
                                ) : (
                                    myRequests.map(request => (
                                        <div key={request.id} style={{
                                            border: `1px solid ${C.border}`, borderRadius: 10,
                                            padding: 12, marginBottom: 12,
                                        }}>
                                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8}}>
                                                <span style={{
                                                    fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                                                    backgroundColor: request.status === "closed" ? C.border : C.primaryLight,
                                                    color: request.status === "closed" ? C.textMuted : C.primary,
                                                }}>
                                                    {request.status === "closed" ? "⚪ Classée" : "🟢 Ouverte"}
                                                </span>
                                                <span style={{fontSize: 11, color: C.textMuted}}>{formatDate(request.created_at)}</span>
                                            </div>
                                            {request.messages.map(msg => (
                                                <div key={msg.id} style={{
                                                    display: "flex",
                                                    justifyContent: msg.sender === "agent" ? "flex-end" : "flex-start",
                                                    marginBottom: 6,
                                                }}>
                                                    <div style={{
                                                        maxWidth: "80%",
                                                        padding: "8px 12px",
                                                        borderRadius: 10,
                                                        fontSize: 13,
                                                        backgroundColor: msg.sender === "agent" ? C.primaryLight : C.bg,
                                                        color: C.text,
                                                    }}>
                                                        {msg.content}
                                                    </div>
                                                </div>
                                            ))}
                                            <div style={{display: "flex", gap: 6, marginTop: 8}}>
                                                <input
                                                    value={replyDrafts[request.id] || ""}
                                                    onChange={e => setReplyDrafts(prev => ({...prev, [request.id]: e.target.value}))}
                                                    placeholder="Répondre…"
                                                    style={{
                                                        flex: 1, padding: "8px 10px", borderRadius: 8,
                                                        border: `1.5px solid ${C.border}`, boxSizing: "border-box",
                                                        fontSize: 13, color: C.text, fontFamily: "inherit",
                                                    }}
                                                />
                                                <button
                                                    onClick={() => sendReply(request.id)}
                                                    disabled={(replyDrafts[request.id] || "").trim() === "" || sendingReplyFor === request.id}
                                                    style={{
                                                        backgroundColor: C.primary, color: "white", border: "none",
                                                        padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                                                        fontWeight: 600, fontSize: 13,
                                                        opacity: ((replyDrafts[request.id] || "").trim() === "" || sendingReplyFor === request.id) ? 0.6 : 1,
                                                    }}
                                                >
                                                    Envoyer
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </>
                        )}
                </Modal>
            )}
        </>
    );
}
